import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Button,
  Chip,
  CircularProgress,
  Alert,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tooltip,
  Tabs,
  Tab,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Checkbox,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemButton,
  Divider,
  Collapse,
} from '@mui/material';
import {
  Assessment,
  Edit,
  Delete,
  ContentCopy,
  Share,
  Visibility,
  Add,
  AutoAwesome,
  Business,
  Star,
  StarBorder,
  Group,
  People,
  PersonOff,
  ExpandMore,
  ExpandLess,
  QuestionAnswer,
} from '@mui/icons-material';
import { auditAPI, AuditSeries, aiBuilderAPI, TemplateClientAssignment } from '../services/api';
import TemplateEditor from '../components/TemplateEditor';
import { useCompany } from '../contexts/CompanyContext';
import { Rating } from '@mui/material';
import LoadingModal from '../components/LoadingModal';

const AuditTemplateManagement: React.FC = () => {
  const navigate = useNavigate();
  const { userPermissions } = useCompany();
  
  const [templates, setTemplates] = useState<AuditSeries[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [deleteDialog, setDeleteDialog] = useState<{ open: boolean; template: AuditSeries | null }>({
    open: false,
    template: null,
  });
  const [cloneDialog, setCloneDialog] = useState<{ open: boolean; template: AuditSeries | null }>({
    open: false,
    template: null,
  });
  const [cloneName, setCloneName] = useState('');
  const [shareDialog, setShareDialog] = useState<{ open: boolean; template: AuditSeries | null; action: 'share' | 'unshare' | null }>({
    open: false,
    template: null,
    action: null,
  });
  const [currentTab, setCurrentTab] = useState<'our' | 'drafts' | 'community'>('our');
  const [ratingDialog, setRatingDialog] = useState<{ open: boolean; template: AuditSeries | null }>({ open: false, template: null });
  const [userRating, setUserRating] = useState<number>(0);
  
  // Client assignment modal state
  const [clientAssignmentDialog, setClientAssignmentDialog] = useState<{
    open: boolean;
    template: AuditSeries | null;
    isLoading: boolean;
  }>({ open: false, template: null, isLoading: false });
  const [clientVisibility, setClientVisibility] = useState<'all' | 'selected' | 'none'>('all');
  const [availableClients, setAvailableClients] = useState<{ id: number; name: string }[]>([]);
  const [selectedClientIds, setSelectedClientIds] = useState<number[]>([]);
  const [assignmentSaving, setAssignmentSaving] = useState(false);
  
  // Loading modals for various operations
  const [isCloning, setIsCloning] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isRating, setIsRating] = useState(false);
  
  // Expanded descriptions state
  const [expandedDescriptions, setExpandedDescriptions] = useState<Set<number>>(new Set());

  useEffect(() => {
    loadTemplates();
  }, []);

  useEffect(() => {
    loadTemplates(currentTab);
  }, [currentTab]);

  const loadTemplates = async (type: 'our' | 'drafts' | 'community' = currentTab) => {
    try {
      setIsLoading(true);
      setError('');
      
      // Load audit series (templates) based on current tab
      // Backend now supports status filtering via query parameter
      let seriesData;
      if (type === 'drafts') {
        // Load draft templates
        seriesData = await auditAPI.getAuditSeries('our', 'draft');
        setTemplates(seriesData);
        console.log(`✅ Loaded ${seriesData.length} draft templates`);
      } else if (type === 'our') {
        // Load published templates
        seriesData = await auditAPI.getAuditSeries('our', 'published');
        setTemplates(seriesData);
        console.log(`✅ Loaded ${seriesData.length} published templates`);
      } else {
        // Community templates (always published)
        seriesData = await auditAPI.getAuditSeries('community', 'published');
        setTemplates(seriesData);
        console.log(`✅ Loaded ${type} templates:`, seriesData);
      }
      
    } catch (error: any) {
      console.error('Error loading templates:', error);
      setError('Failed to load review templates. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateTemplate = () => {
    navigate('/ai-builder');
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: 'our' | 'drafts' | 'community') => {
    setCurrentTab(newValue);
  };

  const handleRateTemplate = (template: AuditSeries) => {
    setRatingDialog({ open: true, template });
    setUserRating(0);
  };

  const submitRating = async () => {
    if (!ratingDialog.template || userRating === 0) {
      setError('Please select a rating');
      return;
    }

    try {
      // Close dialog and show loading modal
      setRatingDialog({ open: false, template: null });
      setIsRating(true);
      
      const result = await auditAPI.rateTemplate(ratingDialog.template.id, userRating);
      
      // Update the template in the list with new average rating
      setTemplates(prev => prev.map(t => 
        t.id === ratingDialog.template!.id 
          ? { ...t, community_rating: result.template_average }
          : t
      ));

      setUserRating(0);
      
      console.log(`✅ Rated template: ${userRating} stars`);
    } catch (error: any) {
      console.error('❌ Error rating template:', error);
      setError('Failed to rate template. Please try again.');
    } finally {
      setIsRating(false);
    }
  };

  const handleEditTemplate = (template: AuditSeries) => {
    navigate(`/admin/templates/edit/${template.id}`);
  };

  const handleViewTemplate = (template: AuditSeries) => {
    navigate(`/admin/templates/view/${template.id}`);
  };

  const handleDeleteTemplate = (template: AuditSeries) => {
    setDeleteDialog({ open: true, template });
  };

  const handleCloneTemplate = async (template: AuditSeries) => {
    setCloneName(`${template.name} (Copy)`);
    setCloneDialog({ open: true, template });
  };

  const confirmCloneTemplate = async () => {
    if (!cloneDialog.template || !cloneName.trim()) return;
    
    try {
      // Close the input dialog and show loading modal
      setCloneDialog({ open: false, template: null });
      setIsCloning(true);
      setError('');
      
      console.log(`🔄 Cloning template "${cloneDialog.template.name}" as "${cloneName.trim()}"`);
      
      // Call clone API endpoint
      await auditAPI.cloneTemplate(cloneDialog.template.id, { name: cloneName.trim() });
      
      // Refresh template list to show the new clone
      await loadTemplates(currentTab);
      
      // If we cloned from community tab, also refresh our templates (where clone will appear)
      if (currentTab === 'community') {
        // Switch to "Our Templates" tab to show the new clone
        setCurrentTab('our');
      }
      
      setCloneName('');
      
      console.log(`✅ Template cloned successfully as "${cloneName.trim()}"`);
      
    } catch (error: any) {
      console.error('❌ Error cloning template:', error);
      setError('Failed to clone template. Please try again.');
    } finally {
      setIsCloning(false);
    }
  };

  const handleToggleSharing = async (template: AuditSeries) => {
    const action = template.is_shared_community ? 'unshare' : 'share';
    setShareDialog({ open: true, template, action });
  };

  const confirmToggleSharing = async () => {
    if (!shareDialog.template || !shareDialog.action) return;
    
    const template = shareDialog.template;
    const newSharingStatus = shareDialog.action === 'share';
    
    try {
      // Close dialog first
      setShareDialog({ open: false, template: null, action: null });
      
      // Optimistic update - immediately update UI
      setTemplates(prev => prev.map(t => 
        t.id === template.id 
          ? { ...t, is_shared_community: newSharingStatus }
          : t
      ));
      
      console.log(`🔄 ${shareDialog.action === 'share' ? 'Sharing' : 'Unsharing'} template: ${template.name}`);
      
      // Call API to update template
      await auditAPI.updateTemplate(template.id, {
        is_shared_community: newSharingStatus
      });
      
      // Show success message
      const message = newSharingStatus 
        ? `Template "${template.name}" shared with community` 
        : `Template "${template.name}" made private`;
      console.log(`✅ ${message}`);
      
      // TODO: Add toast notification here when we implement it
      
    } catch (error: any) {
      console.error('❌ Error toggling sharing:', error);
      
      // Revert optimistic update on error
      setTemplates(prev => prev.map(t => 
        t.id === template.id 
          ? { ...t, is_shared_community: !newSharingStatus }
          : t
      ));
      
      setError('Failed to update sharing settings. Please try again.');
    }
  };

  const confirmDelete = async () => {
    if (!deleteDialog.template) return;

    const templateId = deleteDialog.template.id;
    const templateName = deleteDialog.template.name;

    try {
      // Close dialog and show loading modal
      setDeleteDialog({ open: false, template: null });
      setIsDeleting(true);
      
      console.log('🗑️ Deleting template:', templateName, 'ID:', templateId);
      
      // Call the delete API
      await aiBuilderAPI.deleteTemplate(templateId);
      
      console.log('✅ API delete successful for template ID:', templateId);
      
      // Optimistically remove template from UI immediately
      console.log('🔄 Filtering templates, removing ID:', templateId);
      console.log('📋 Current templates count:', templates.length);
      
      setTemplates(prev => {
        const filtered = prev.filter(t => t.id !== templateId);
        console.log('✅ Filtered templates count:', filtered.length);
        return filtered;
      });
      
    } catch (error: any) {
      console.error('❌ Error deleting template:', error);
      setError('Failed to delete template. Please try again.');
    } finally {
      setIsDeleting(false);
    }
  };



  // Handle opening client assignment modal
  const handleManageClientAccess = async (template: AuditSeries) => {
    setClientAssignmentDialog({ open: true, template, isLoading: true });
    setError('');
    
    try {
      const response = await auditAPI.getTemplateClientAssignments(template.id);
      setClientVisibility(response.client_visibility);
      setAvailableClients(response.available_clients);
      setSelectedClientIds(response.assigned_clients.map(c => c.id));
      setClientAssignmentDialog(prev => ({ ...prev, isLoading: false }));
      console.log(`✅ Loaded client assignments for template: ${template.name}`);
    } catch (error: any) {
      console.error('❌ Error loading client assignments:', error);
      setError('Failed to load client assignments. Please try again.');
      setClientAssignmentDialog({ open: false, template: null, isLoading: false });
    }
  };

  // Handle saving client assignment changes
  const handleSaveClientAssignments = async () => {
    if (!clientAssignmentDialog.template) return;
    
    setAssignmentSaving(true);
    setError('');
    
    try {
      const response = await auditAPI.updateTemplateClientVisibility(
        clientAssignmentDialog.template.id,
        {
          client_visibility: clientVisibility,
          assigned_client_ids: clientVisibility === 'selected' ? selectedClientIds : []
        }
      );
      
      // Update the template in the list with new visibility info
      setTemplates(prev => prev.map(t => 
        t.id === clientAssignmentDialog.template!.id 
          ? { 
              ...t, 
              client_visibility: response.client_visibility,
              assigned_client_count: response.assigned_count
            }
          : t
      ));
      
      console.log(`✅ Updated client visibility for template: ${clientAssignmentDialog.template.name}`);
      setClientAssignmentDialog({ open: false, template: null, isLoading: false });
      
    } catch (error: any) {
      console.error('❌ Error saving client assignments:', error);
      setError('Failed to save client assignments. Please try again.');
    } finally {
      setAssignmentSaving(false);
    }
  };

  // Handle client checkbox toggle
  const handleClientToggle = (clientId: number) => {
    setSelectedClientIds(prev => 
      prev.includes(clientId) 
        ? prev.filter(id => id !== clientId)
        : [...prev, clientId]
    );
  };
  
  // Toggle description expansion
  const toggleDescription = (templateId: number) => {
    setExpandedDescriptions(prev => {
      const newSet = new Set(prev);
      if (newSet.has(templateId)) {
        newSet.delete(templateId);
      } else {
        newSet.add(templateId);
      }
      return newSet;
    });
  };

  // Get client visibility display chip
  const getClientVisibilityChip = (template: AuditSeries) => {
    if (template.client_visibility === 'selected') {
      const count = template.assigned_client_count || 0;
      return (
        <Chip 
          icon={<People sx={{ fontSize: '16px !important' }} />} 
          label={`${count} Client${count !== 1 ? 's' : ''}`} 
          size="small" 
          color="info"
          variant="outlined"
        />
      );
    }
    if (template.client_visibility === 'none') {
      return (
        <Chip 
          icon={<PersonOff sx={{ fontSize: '16px !important' }} />} 
          label="No Clients" 
          size="small" 
          color="warning"
          variant="outlined"
        />
      );
    }
    return (
      <Chip 
        icon={<Group sx={{ fontSize: '16px !important' }} />} 
        label="All Clients" 
        size="small" 
        color="default"
        variant="outlined"
      />
    );
  };

  const getTemplateTypeChip = (template: AuditSeries) => {
    if (template.is_ai_generated) {
      return <Chip icon={<AutoAwesome />} label="AI Generated" size="small" color="secondary" />;
    }
    return <Chip icon={<Business />} label="Manual" size="small" color="default" />;
  };

  const getSharingChip = (template: AuditSeries) => {
    if (template.is_shared_community) {
      return <Chip label="Shared" size="small" color="success" />;
    }
    return <Chip label="Private" size="small" color="default" />;
  };

  if (isLoading) {
    return (
      <Container maxWidth="lg">
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h5" component="h1">
            Review Templates
          </Typography>
          {/* Fixed-width container to prevent layout shift */}
          <Box sx={{ width: '220px', display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="contained"
              size="small"
              startIcon={<Add />}
              onClick={handleCreateTemplate}
              sx={{ 
                visibility: currentTab === 'our' ? 'visible' : 'hidden'
              }}
            >
              Create New Template
            </Button>
          </Box>
        </Box>
        <Typography variant="body1" color="text.secondary">
          Manage your review templates, edit questions, and control sharing settings
        </Typography>
        
        {/* Template Type Tabs */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mt: 3 }}>
          <Tabs value={currentTab} onChange={handleTabChange}>
            <Tab label="Our Templates" value="our" />
            <Tab label="Our Draft Templates" value="drafts" />
            <Tab label="Community Templates" value="community" />
          </Tabs>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Template Name</TableCell>
              <TableCell>Sharing</TableCell>
              {currentTab === 'our' && <TableCell>Clients</TableCell>}
              <TableCell>Usage Count</TableCell>
              <TableCell>Rating</TableCell>
              <TableCell>Created</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {templates.map((template) => (
              <React.Fragment key={template.id}>
                <TableRow hover>
                  <TableCell>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Assessment color="primary" />
                        <Typography variant="subtitle2">{template.name}</Typography>
                        {template.description && (
                          <Tooltip title={expandedDescriptions.has(template.id) ? 'Hide description' : 'Show description'}>
                            <IconButton 
                              size="small" 
                              onClick={() => toggleDescription(template.id)}
                              sx={{ ml: 0.5 }}
                            >
                              {expandedDescriptions.has(template.id) ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <QuestionAnswer fontSize="small" sx={{ color: 'text.secondary' }} />
                        <Typography variant="caption" color="text.secondary">
                          {template.question_count || 0} questions
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>{getSharingChip(template)}</TableCell>
                {currentTab === 'our' && (
                  <TableCell>{getClientVisibilityChip(template)}</TableCell>
                )}
                <TableCell>{template.usage_count || 0}</TableCell>
                <TableCell>
                  <Box 
                    sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: 0.5,
                      cursor: currentTab === 'community' ? 'pointer' : 'default'
                    }}
                    onClick={() => currentTab === 'community' && handleRateTemplate(template)}
                  >
                    {(template.community_rating || 0) > 0 ? (
                      <>
                        <Star color="warning" fontSize="small" />
                        <Typography variant="body2">{(template.community_rating || 0).toFixed(1)}</Typography>
                      </>
                    ) : currentTab === 'community' ? (
                      <>
                        <StarBorder color="action" fontSize="small" />
                        <Typography variant="body2" color="primary">Rate</Typography>
                      </>
                    ) : (
                      <>
                        <StarBorder color="disabled" fontSize="small" />
                        <Typography variant="body2" color="text.secondary">—</Typography>
                      </>
                    )}
                  </Box>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary">
                    {template.created_at ? new Date(template.created_at).toLocaleDateString() : '—'}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Tooltip title="View Template">
                      <IconButton size="small" color="primary" onClick={() => handleViewTemplate(template)}>
                        <Visibility />
                      </IconButton>
                    </Tooltip>
                    
                    {/* Our Templates: Full management capabilities */}
                    {currentTab === 'our' && (
                      <>
                        <Tooltip title="Edit Template">
                          <IconButton size="small" onClick={() => handleEditTemplate(template)}>
                            <Edit />
                          </IconButton>
                        </Tooltip>
                        {/* Manage client access */}
                        {userPermissions?.can_manage_templates && (
                          <Tooltip title="Manage Client Access">
                            <IconButton size="small" color="info" onClick={() => handleManageClientAccess(template)}>
                              <People />
                            </IconButton>
                          </Tooltip>
                        )}
                        {/* Only network admins can toggle sharing */}
                        {userPermissions?.can_manage_templates && (
                          <Tooltip title="Toggle Community Sharing">
                            <IconButton size="small" onClick={() => handleToggleSharing(template)}>
                              <Share />
                            </IconButton>
                          </Tooltip>
                        )}
                        <Tooltip title="Delete Template">
                          <IconButton size="small" color="error" onClick={() => handleDeleteTemplate(template)}>
                            <Delete />
                          </IconButton>
                        </Tooltip>
                      </>
                    )}
                    
                    {/* Draft Templates: Resume AI Builder or Edit directly */}
                    {currentTab === 'drafts' && (
                      <>
                        {template.wizard_step && (
                          <Tooltip title="Resume AI Builder">
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={<AutoAwesome />}
                              onClick={() => navigate(`/ai-builder?resume=${template.id}`)}
                            >
                              Resume
                            </Button>
                          </Tooltip>
                        )}
                        <Tooltip title="Edit Template">
                          <IconButton size="small" onClick={() => handleEditTemplate(template)}>
                            <Edit />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete Draft">
                          <IconButton size="small" color="error" onClick={() => handleDeleteTemplate(template)}>
                            <Delete />
                          </IconButton>
                        </Tooltip>
                      </>
                    )}
                    
                    {/* Clone is available for all templates */}
                    <Tooltip title="Clone Template">
                      <IconButton size="small" onClick={() => handleCloneTemplate(template)}>
                        <ContentCopy />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </TableCell>
              </TableRow>
              {/* Collapsible description row */}
              {template.description && (
                <TableRow>
                  <TableCell 
                    colSpan={currentTab === 'our' ? 7 : 6} 
                    sx={{ py: 0, borderBottom: expandedDescriptions.has(template.id) ? undefined : 0 }}
                  >
                    <Collapse in={expandedDescriptions.has(template.id)} timeout="auto" unmountOnExit>
                      <Box sx={{ py: 2, px: 2, bgcolor: 'action.hover', borderRadius: 1, my: 1 }}>
                        <Typography variant="body2" color="text.secondary">
                          {template.description}
                        </Typography>
                      </Box>
                    </Collapse>
                  </TableCell>
                </TableRow>
              )}
              </React.Fragment>
            ))}
            {templates.length === 0 && (
              <TableRow>
                <TableCell colSpan={currentTab === 'our' ? 7 : 6} align="center" sx={{ py: 4 }}>
                  <Typography variant="body1" color="text.secondary">
                    {currentTab === 'our' 
                      ? 'No published templates found. Create your first review template to get started!'
                      : currentTab === 'drafts'
                      ? 'No draft templates found. Start creating a template in the AI Builder to see drafts here.'
                      : 'No community templates are currently shared. Check back later for templates shared by other consultants!'
                    }
                  </Typography>
                  {currentTab === 'our' && (
                    <Button
                      variant="outlined"
                      startIcon={<Add />}
                      onClick={handleCreateTemplate}
                      sx={{ mt: 2, textTransform: 'none' }}
                    >
                      Create Template
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialog.open} onClose={() => setDeleteDialog({ open: false, template: null })}>
        <DialogTitle>Delete Template</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete "{deleteDialog.template?.name}"? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false, template: null })}>
            Cancel
          </Button>
          <Button onClick={confirmDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Clone Template Dialog */}
      <Dialog open={cloneDialog.open} onClose={() => setCloneDialog({ open: false, template: null })}>
        <DialogTitle>Clone Template</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>
            Create a copy of "{cloneDialog.template?.name}" with a new name:
          </Typography>
          <TextField
            autoFocus
            fullWidth
            label="New Template Name"
            value={cloneName}
            onChange={(e) => setCloneName(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && confirmCloneTemplate()}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCloneDialog({ open: false, template: null })}>
            Cancel
          </Button>
          <Button 
            onClick={confirmCloneTemplate} 
            variant="contained"
            disabled={!cloneName.trim()}
          >
            Clone Template
          </Button>
        </DialogActions>
      </Dialog>

      {/* Share Template Confirmation Dialog */}
      <Dialog open={shareDialog.open} onClose={() => setShareDialog({ open: false, template: null, action: null })}>
        <DialogTitle>
          {shareDialog.action === 'share' ? 'Share Template with Community' : 'Make Template Private'}
        </DialogTitle>
        <DialogContent>
          <Typography>
            {shareDialog.action === 'share' 
              ? `⚠️ Are you sure you want to share "${shareDialog.template?.name}" with the community? This will make your template visible to all platform users.`
              : `Are you sure you want to make "${shareDialog.template?.name}" private? It will no longer be visible to other users.`
            }
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShareDialog({ open: false, template: null, action: null })}>
            Cancel
          </Button>
          <Button 
            onClick={confirmToggleSharing} 
            variant="contained"
            color={shareDialog.action === 'share' ? 'warning' : 'primary'}
          >
            {shareDialog.action === 'share' ? 'Share with Community' : 'Make Private'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Rate Template Dialog */}
      <Dialog open={ratingDialog.open} onClose={() => setRatingDialog({ open: false, template: null })}>
        <DialogTitle>
          Rate Template: {ratingDialog.template?.name}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 3 }}>
            How would you rate this template? Your rating helps other consultants discover quality templates.
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
            <Rating
              value={userRating}
              onChange={(event, newValue) => setUserRating(newValue || 0)}
              size="large"
            />
            <Typography variant="body2" color="text.secondary">
              {userRating === 0 && 'Select a rating'}
              {userRating === 1 && 'Poor'}
              {userRating === 2 && 'Fair'}
              {userRating === 3 && 'Good'}
              {userRating === 4 && 'Very Good'}
              {userRating === 5 && 'Excellent'}
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRatingDialog({ open: false, template: null })}>
            Cancel
          </Button>
          <Button onClick={submitRating} variant="contained" color="primary" disabled={userRating === 0}>
            Submit Rating
          </Button>
        </DialogActions>
      </Dialog>

      {/* Manage Client Access Dialog */}
      <Dialog 
        open={clientAssignmentDialog.open} 
        onClose={() => !assignmentSaving && setClientAssignmentDialog({ open: false, template: null, isLoading: false })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <People color="primary" />
            Manage Client Access
          </Box>
        </DialogTitle>
        <DialogContent>
          {clientAssignmentDialog.isLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Control which clients can see and use the template: <strong>{clientAssignmentDialog.template?.name}</strong>
              </Typography>
              
              <FormControl component="fieldset" sx={{ mb: 2, width: '100%' }}>
                <RadioGroup
                  value={clientVisibility}
                  onChange={(e) => setClientVisibility(e.target.value as 'all' | 'selected' | 'none')}
                >
                  <FormControlLabel 
                    value="all" 
                    control={<Radio />} 
                    label={
                      <Box>
                        <Typography variant="body1">All Clients</Typography>
                        <Typography variant="caption" color="text.secondary">
                          Every client in your network can see and use this template
                        </Typography>
                      </Box>
                    }
                  />
                  <FormControlLabel 
                    value="selected" 
                    control={<Radio />} 
                    label={
                      <Box>
                        <Typography variant="body1">Selected Clients Only</Typography>
                        <Typography variant="caption" color="text.secondary">
                          Only the clients you select below can see and use this template
                        </Typography>
                      </Box>
                    }
                  />
                  <FormControlLabel 
                    value="none" 
                    control={<Radio />} 
                    label={
                      <Box>
                        <Typography variant="body1">No Clients (Template Only)</Typography>
                        <Typography variant="caption" color="text.secondary">
                          Not available to any clients - save as a draft template for later
                        </Typography>
                      </Box>
                    }
                  />
                </RadioGroup>
              </FormControl>

              {clientVisibility === 'selected' && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Select Clients ({selectedClientIds.length} selected)
                  </Typography>
                  
                  {availableClients.length === 0 ? (
                    <Alert severity="info" sx={{ mt: 1 }}>
                      No client companies found. Add clients in the Client Companies section first.
                    </Alert>
                  ) : (
                    <Paper variant="outlined" sx={{ maxHeight: 250, overflow: 'auto' }}>
                      <List dense>
                        {availableClients.map((client) => (
                          <ListItem key={client.id} disablePadding>
                            <ListItemButton 
                              onClick={() => handleClientToggle(client.id)}
                              sx={{ py: 0.5 }}
                            >
                              <ListItemIcon sx={{ minWidth: 36 }}>
                                <Checkbox
                                  edge="start"
                                  checked={selectedClientIds.includes(client.id)}
                                  tabIndex={-1}
                                  disableRipple
                                  size="small"
                                />
                              </ListItemIcon>
                              <ListItemText 
                                primary={client.name}
                                primaryTypographyProps={{ variant: 'body2' }}
                              />
                            </ListItemButton>
                          </ListItem>
                        ))}
                      </List>
                    </Paper>
                  )}
                  
                  {clientVisibility === 'selected' && selectedClientIds.length === 0 && availableClients.length > 0 && (
                    <Alert severity="warning" sx={{ mt: 2 }}>
                      No clients selected. This template won't be visible to any clients until you select at least one.
                    </Alert>
                  )}
                </>
              )}

              {clientVisibility === 'none' && (
                <Alert severity="info" sx={{ mt: 2 }}>
                  This template is saved as a draft and is not visible to any clients. You can change this setting at any time.
                </Alert>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setClientAssignmentDialog({ open: false, template: null, isLoading: false })}
            disabled={assignmentSaving}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleSaveClientAssignments} 
            variant="contained" 
            color="primary"
            disabled={clientAssignmentDialog.isLoading || assignmentSaving}
          >
            {assignmentSaving ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Loading Modals */}
      <LoadingModal
        open={isCloning}
        title="Cloning Template"
        message="Creating a copy of the template. This may take a few seconds."
      />
      
      <LoadingModal
        open={isDeleting}
        title="Deleting Template"
        message="Removing the template and all associated data."
      />
      
      <LoadingModal
        open={isRating}
        title="Submitting Rating"
        message="Saving your rating to the community."
      />

    </Container>
  );
};

export default AuditTemplateManagement; 